/**
 * Helpers for building the `clientMeta` block + merging ambient feedback
 * context (from FeedbackProvider) with per-call overrides.
 *
 * The ambient context lets a deeply-nested component (e.g. a hover-reveal
 * thumb on a timeline event) emit feedback without re-passing caseId,
 * sessionKey, etc. through every prop layer.
 */

import type { FeedbackClientMeta, FeedbackSurfaceContext } from "@/types/feedback";

/** Build the `clientMeta` block from the current browser environment. */
export function buildClientMeta(): FeedbackClientMeta {
  // Vite injects VITE_APP_VERSION at build time via vite.config.ts `define`.
  // Falls back to "dev" when running the dev server pre-build.
  const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev";
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
  const locale =
    typeof navigator !== "undefined" && navigator.language ? navigator.language : "pt-BR";
  const viewport =
    typeof window !== "undefined"
      ? { width: window.innerWidth, height: window.innerHeight }
      : undefined;
  return {
    appVersion,
    userAgent,
    locale,
    viewport,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Merge an ambient surface context with per-call overrides.
 *
 * Override fields win when present (so a chat message bubble can attach
 * its own runId / messageExcerpt without losing the case-level sessionKey
 * from the provider).
 */
export function mergeSurfaceContext(
  ambient: FeedbackSurfaceContext | undefined,
  override: FeedbackSurfaceContext | undefined,
): FeedbackSurfaceContext {
  if (!ambient) return override ?? {};
  if (!override) return ambient;
  return {
    ...ambient,
    ...override,
    extra: { ...(ambient.extra ?? {}), ...(override.extra ?? {}) },
  };
}
